import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Button } from "./Button";

export type ConfirmOptions = {
  /** Título del diálogo (obligatorio). */
  title: string;
  /** Cuerpo opcional que explica la consecuencia de la acción. */
  message?: ReactNode;
  /** Etiqueta del botón de confirmación. Default: común "Confirmar". */
  confirmLabel?: string;
  /** Etiqueta del botón de cancelar. Default: común "Cancelar". */
  cancelLabel?: string;
  /** Estiliza la acción como destructiva (rojo). */
  danger?: boolean;
};

type Pending = ConfirmOptions & { resolve: (ok: boolean) => void };

const ConfirmContext = createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null);

/**
 * Diálogo de confirmación imperativo, reemplazo premium de window.confirm.
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: "¿Borrar?", danger: true }))) return;
 *
 * Resuelve true al confirmar; false al cancelar, hacer clic afuera o Escape.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<Pending | null>(null);

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setPending({ ...opts, resolve });
    });
  }, []);

  const close = useCallback(
    (ok: boolean) => {
      setPending((cur) => {
        cur?.resolve(ok);
        return null;
      });
    },
    [],
  );

  // Cierre con teclado (el foco inicial lo da autoFocus en el botón).
  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close(false);
      } else if (e.key === "Enter") {
        e.preventDefault();
        close(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pending, close]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending &&
        createPortal(
          <div
            onClick={() => close(false)}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-[110] animate-fade-in"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="confirm-dialog-title"
              className="bg-forja-surface border border-forja-border rounded-xl p-6 max-w-md w-full shadow-card animate-slide-up"
            >
              <h3 id="confirm-dialog-title" className="text-lg font-bold mb-1">
                {pending.title}
              </h3>
              {pending.message && (
                <div className="text-sm text-white/60 mt-1 mb-1 leading-relaxed">
                  {pending.message}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-5">
                <Button variant="ghost" onClick={() => close(false)}>
                  {pending.cancelLabel ?? t("common.cancel")}
                </Button>
                <Button
                  autoFocus
                  variant={pending.danger ? "danger" : "primary"}
                  onClick={() => close(true)}
                >
                  {pending.confirmLabel ?? t("common.confirm", { defaultValue: "Confirmar" })}
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm debe usarse dentro de <ConfirmProvider>");
  return ctx;
}
